import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, graphql } from 'react-relay';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { DaysheetlyButton } from '@/components/ui/DaysheetlyButton';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  WorkspaceFlowBrand,
  WorkspaceFlowShell,
} from '@/features/workspace/components/WorkspaceFlowShell';
import { ArrowRight, Building2, Sparkles } from 'lucide-react';
import type { OnboardingWorkspacePageCreateMutation as OnboardingWorkspaceCreateMutationType } from '@/__generated__/OnboardingWorkspacePageCreateMutation.graphql';

const formSchema = z.object({
  name: z.string().min(1, 'Workspace name is required').max(120),
});
type FormValues = z.infer<typeof formSchema>;

const createWorkspaceMutation = graphql`
  mutation OnboardingWorkspacePageCreateMutation($name: String!) {
    createWorkspace(name: $name) {
      id
      name
    }
  }
`;

export function OnboardingWorkspacePage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, setActiveWorkspaceId } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);

  // If we land here without an active session at all (token expired or
  // cleared by another tab), bounce to /login with a `next` so the user
  // comes back here after authenticating. This covers the long-tail
  // scenario that produced the "Not authenticated" error message in
  // sessions where localStorage was wiped between mount and submit.
  if (!isAuthenticated) {
    setTimeout(() => {
      navigate(`/login?next=${encodeURIComponent('/onboarding/workspace')}`, {
        replace: true,
      });
    }, 0);
    return null;
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: user ? `${user.name.split(' ')[0]}'s Workspace` : '' },
  });

  const [commit] = useMutation<OnboardingWorkspaceCreateMutationType>(
    createWorkspaceMutation,
  );

  const onSubmit = (data: FormValues) => {
    setServerError(null);
    commit({
      variables: { name: data.name.trim() },
      onCompleted: (response, errors) => {
        if (errors?.[0]) {
          // The most common failing code here is "Not authenticated" —
          // localStorage probably was reset between page mount and
          // submit (e.g., by another tab clearing auth). Send the
          // user back to login with a next param so the flow
          // recovers instead of dead-ending.
          if (errors[0].message.toLowerCase().includes('not authenticated')) {
            navigate(
              `/login?next=${encodeURIComponent('/onboarding/workspace')}`,
              { replace: true },
            );
            return;
          }
          setServerError(errors[0].message);
          return;
        }
        const workspaceId = response.createWorkspace.id;
        setActiveWorkspaceId(workspaceId);
        toast.success(`Workspace "${response.createWorkspace.name}" created`);
        // Hard navigation so the WorkspaceProvider picks up the new
        // member status and re-fetches the workspaces list.
        navigate('/dashboard', { replace: true });
      },
      onError: (err) => setServerError(err.message),
    });
  };

  return (
    <WorkspaceFlowShell>
      <div
        data-testid="onboarding-workspace-card"
        className="w-full rounded-[28px] border bg-background/95 px-6 py-8 shadow-2xl shadow-blue-900/20 backdrop-blur sm:px-10 sm:py-12 lg:px-14 lg:py-14"
      >
        <WorkspaceFlowBrand />
        <div className="text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Create your workspace
          </h1>
          <p className="mx-auto mt-4 max-w-md text-base leading-7 text-muted-foreground sm:text-lg">
            This will be the home for your projects, team, and day sheets.
          </p>
        </div>

        <div className="relative my-10 flex justify-center">
          <div className="flex h-28 w-28 items-center justify-center rounded-full bg-blue-100/80 text-blue-700">
            <Building2 className="h-14 w-14" strokeWidth={1.5} aria-hidden="true" />
          </div>
          <Sparkles className="absolute left-[calc(50%+70px)] top-3 h-5 w-5 text-amber-400" aria-hidden="true" />
          <Sparkles className="absolute left-[calc(50%-92px)] top-16 h-4 w-4 text-blue-400" aria-hidden="true" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="workspace-name" className="text-base">Workspace name</Label>
            <Input
              id="workspace-name"
              data-testid="onboarding-workspace-name"
              placeholder="Acme Studio"
              autoFocus
              className="h-14 text-base"
              {...register('name')}
            />
            {errors.name ? (
              <p className="text-sm text-destructive" data-testid="onboarding-workspace-name-error">
                {errors.name.message}
              </p>
            ) : null}
            {serverError ? (
              <p className="text-sm text-destructive">{serverError}</p>
            ) : null}
          </div>
          <DaysheetlyButton
            type="submit"
            size="lg"
            fullWidth
            loading={isSubmitting}
            data-testid="onboarding-workspace-submit"
            endIcon={<ArrowRight className="h-5 w-5" aria-hidden="true" />}
          >
            {isSubmitting ? 'Creating…' : 'Create workspace'}
          </DaysheetlyButton>
        </form>
        <div className="mt-8 flex items-center justify-center gap-4 text-sm text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          <span className="flex items-center gap-2 whitespace-nowrap">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground">1</span>
            Step 1 of 1
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>
      </div>
    </WorkspaceFlowShell>
  );
}
